import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageSeo } from '../components/Seo';
import { useBlog } from '../context/BlogContext';
import { formatBlogDate, getBlogContentHtml, stripBlogHtml } from '../utils/blog';
import { breadcrumbJsonLd } from '../utils/structuredData';

export default function BlogPostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { getPostBySlug, loadPostBySlug, isLoading } = useBlog();
  const resolvedPost = slug ? getPostBySlug(slug) : undefined;
  const [ready, setReady] = useState(Boolean(resolvedPost));

  useEffect(() => {
    if (!slug) {
      setReady(true);
      return;
    }

    if (getPostBySlug(slug)) {
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);

    void loadPostBySlug(slug).finally(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [slug, getPostBySlug, loadPostBySlug]);

  const post = slug ? getPostBySlug(slug) : undefined;

  if (!ready || (isLoading && !post)) {
    return (
      <div className="container empty-state">
        <p>Yazı yükleniyor...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container empty-state">
        <PageSeo title="Yazı Bulunamadı" description="Aradığınız blog yazısı bulunamadı." noindex />
        <h1>Yazı bulunamadı</h1>
        <Link to="/blog" className="btn btn-primary">
          Bloga Dön
        </Link>
      </div>
    );
  }

  const postPath = `/blog/${post.slug}`;
  const contentHtml = getBlogContentHtml(post.content);
  const seoDescription = post.excerpt ?? stripBlogHtml(contentHtml).slice(0, 160);

  return (
    <div className="container blog-detail">
      <PageSeo
        title={post.title}
        description={seoDescription}
        path={postPath}
        type="article"
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Ana Sayfa', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: postPath },
          ]),
        ]}
      />

      <Link to="/blog" className="back-link">
        <ArrowLeft size={16} aria-hidden />
        Bloga Dön
      </Link>

      <article className="blog-article">
        <header className="blog-article-header">
          <div className="blog-card-meta">
            <time dateTime={post.publishedAt ?? post.createdAt}>
              {formatBlogDate(post.publishedAt ?? post.createdAt)}
            </time>
            {post.author && <span>{post.author}</span>}
          </div>
          <h1>{post.title}</h1>
          {post.excerpt && <p className="blog-article-excerpt">{post.excerpt}</p>}
        </header>

        <div
          className="blog-article-content blog-article-content-rich"
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />
      </article>
    </div>
  );
}
